import React from 'react';
import Popover from 'react-bootstrap/Popover';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import { formatDecimalTime } from '../../../util/common';

/**
 * Formats internal work hour time period enum types into clean human-readable labels for modals.
 *
 * @param {string} timeType - Work hour time period type (e.g., 'STANDARD', 'OVERTIME_1', 'PUBLIC_HOLIDAY').
 * @returns {string} User-facing label (e.g., 'Standard', 'OVERTIME', 'HOLIDAY', 'Unpaid Allowance').
 */
export const formatTimeTypeLabel = (timeType) => {
    if (!timeType) return '';
    if (timeType === 'STANDARD') return 'Standard';
    if (timeType === 'OVERTIME_1' || timeType === 'OVERTIME') return 'OVERTIME';
    if (timeType === 'PUBLIC_HOLIDAY' || timeType === 'HOLIDAY') return 'HOLIDAY';
    if (timeType === 'UNPAID_ALLOWANCE') return 'Unpaid Allowance';
    return timeType.replace(/_/g, ' ');
};

/**
 * Formats internal work hour time period enum types into compact table abbreviations.
 *
 * @param {string} timeType - Work hour time period type.
 * @returns {string} Short abbreviation ('Std', 'Hol', 'Unpaid', 'OT').
 */
export const formatPeriodShortLabel = (timeType) => {
    if (timeType === 'STANDARD') return 'Std';
    if (timeType === 'PUBLIC_HOLIDAY' || timeType === 'HOLIDAY' || timeType?.includes('HOLIDAY')) return 'Hol';
    if (timeType === 'UNPAID_ALLOWANCE') return 'Unpaid';
    return 'OT';
};

/**
 * Renders the table column cell for work hour breakdown, including time periods and unclassified warning overlay.
 *
 * @param {Object} rowData - Allocation row data with work_hour_breakdown and entryHours.
 * @returns {React.ReactNode} Formatted breakdown cell content.
 */
export const renderBreakdownCell = (rowData) => {
    const b = rowData?.work_hour_breakdown;
    if (!b?.time_periods?.length) return '—';

    const periodLabels = b.time_periods.map(
        (p) => `${formatDecimalTime(p.hours)} ${formatPeriodShortLabel(p.time_type)}`
    );
    const classifiedHours = b.time_periods.reduce((sum, p) => sum + (parseFloat(p.hours) || 0), 0);
    const unclassifiedHours = (rowData?.entryHours || 0) - classifiedHours;
    const hasUnclassified = unclassifiedHours > 0.05;

    if (!hasUnclassified) return periodLabels.join(' | ');

    return (
        <OverlayTrigger
            placement="top"
            overlay={
                <Popover>
                    <Popover.Body style={{ fontSize: '0.8rem' }}>
                        Some worked hours fall outside the configured work hour periods and are not included in the cost breakdown. Review the Work Hour configuration to ensure full-day coverage.
                    </Popover.Body>
                </Popover>
            }
        >
            <span>
                {periodLabels.join(' | ')}
                {' | '}
                <span style={{ color: '#f59e0b', cursor: 'help' }}>
                    ⚠ {formatDecimalTime(unclassifiedHours)} Unclassified
                </span>
            </span>
        </OverlayTrigger>
    );
};
